import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';

import { MtsAudioModule } from '@vlabs/api-bindings/src/mtsAudioModule';
import { apiContainer } from '@vlabs/api-bindings';
import { VideoJSPlayer } from '@vlabs/monokit/shared/ui/video-player/VideoJSPlayer';
import {
  CrossCircleFilledIcon, CheckCircleFilledIcon, PlayFilledIcon, PauseIcon, RepeatIcon,
} from '@vlabs/icons';
import segment from '@vlabs/shared/assets/images/audio-check/segment.svg';
import Loader from '@vlabs/shared/legacy-components/loader/Loader';
import { selectAppConfig } from '@vlabs/shared/selectors/appSelectors';
import {
  Page, Tag, Control, ButtonScrimGroup, useModal, Breadcrumbs, Breadcrumb,
} from '@vlabs/uikit';
import { SUPPORTED_AUDIO_MIME } from '../components/video-and-audio-form/constants';
import cn from 'classnames';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ResultPanel, VideoAndAudioForm, Timeline } from '../components';
import type { TAntispoofingResponse, TVideoAndAudioResultPageProps, TLunaClientShim } from './types';
import st from './VideoAndAudioResultPage.module.sass';
import { formatTime } from '../utils/formatTime';

const useQuery = () => new URLSearchParams(useLocation().search);
const msToSec = (ms: number) => ms / 1000;

const findSegmentForTime = (
  segments: TAntispoofingResponse['data']['video'] | TAntispoofingResponse['data']['audio'],
  tSec: number) => {
  if (!segments?.length) return null;

  const tMs = tSec * 1000;

  return segments.find(
    (seg) => tMs >= seg.start_time_ms && tMs <= seg.end_time_ms,
  ) || null;
};

const mapVerdict = (verdict: string, t: TFunction) => {
  const isFake = verdict === 'ATTACK_DETECTED';
  const context = isFake ? 'fake' : undefined;

  return {
    value: isFake ? 'fake' : 'real',
    labelVideo: t(
      'checks:result video and audio.label.video',
      { context }
    ),
    labelAudio: t(
      'checks:result video and audio.label.audio',
      { context }
    ),
    icon: isFake ? <CrossCircleFilledIcon /> : <CheckCircleFilledIcon />,
    tagKind: isFake ? 'negative' : 'positive' as const,
  };
};

function VideoAndAudioResultPageComponent({
}): React.FC<TVideoAndAudioResultPageProps> {
  const antispoofingClientRef = useRef<MtsAudioModule>(null);
  const { t } = useTranslation();
  const videoAudioModal = useModal();
  const query = useQuery();
  const rawFileUrl = query.get('file_url');
  const fileUrl = rawFileUrl ? decodeURIComponent(rawFileUrl) : null;

  const [playerSrc, setPlayerSrc] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [fileType, setFileType] = useState<string>('');
  const [playing, setPlaying] = useState(false);
  const [result, setResult] = useState<TAntispoofingResponse['data'] | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState(false);
  const antispoofingConfig = useSelector(selectAppConfig)?.antispoofing;

  const hasVideo = Array.isArray(result?.video) && result.video.length > 0;
  const hasAudio = Array.isArray(result?.audio) && result.audio.length > 0;
  const isAudioResultOnly = hasAudio && !hasVideo;
  const isAudioFile = SUPPORTED_AUDIO_MIME.includes(fileType);

  useEffect(() => {
    if (!fileUrl) {
      setPlayerSrc(null);
      setFileName(undefined);
      setFileType('');
      setResult(null);
      setCurrentTime(0);
      setPlaying(false);
      setError(false);
      return;
    }

    const fetchFile = async () => {
      try {
        const lunaClient = (apiContainer as unknown as { lunaClient: TLunaClientShim }).lunaClient;

        const objectId = fileUrl.split('/').pop();
        if (!objectId) return;

        const objectResp = await lunaClient.objects.get(objectId, {
          responseType: 'blob',
          params: {
            with_meta: 1,
          },
        });

        setFileName(objectResp.headers['x-luna-meta-filename'] ?? undefined);

        const blob = objectResp?.data as Blob;
        setFileType(blob.type || '');

        const blobUrl = URL.createObjectURL(blob);
        setPlayerSrc(blobUrl);
      } catch (e) {
        console.error('Failed to load video blob', e);
      }
    };

    const fetchData = async () => {
      try {
        const url = antispoofingConfig?.url;
        if (!url) {
          toast.error('Antispoofing URL is not configured');
          setError(true);
          return;
        }
        antispoofingClientRef.current ??= new MtsAudioModule(antispoofingConfig?.url);
        const response: TAntispoofingResponse = await antispoofingClientRef.current.antispoofing.check({
          // cсылка на файл на внешнем хранилище, по которой можно его скачать
          file_url: fileUrl,
          sync_get_data: false,
          analytic_params: {
            rate: { period: 1, unit: 'frame' },
          },
        });

        setResult(response.data);
      } catch (e: any) {
        toast.error(e?.message ?? 'Request failed');
        setError(true);
      }
    };

    fetchFile();
    fetchData();
  }, [fileUrl]);

  const durationSec = useMemo(() => {
    const sec = Number(result?.meta_data?.Format?.duration ?? 0);
    return sec > 0 ? sec : 0;
  }, [result]);

  const videoTracks = useMemo(() => {
    if (!result?.video?.length) return [];
    return [
      {
        title: t('checks:result video and audio.title.video').toUpperCase(),
        segments: result.video.map((seg) => ({
          startTime: msToSec(seg.start_time_ms),
          endTime: msToSec(seg.end_time_ms),
          status: mapVerdict(seg.verdict, t).value,
          mediaDuration: durationSec,
        })),
      },
    ];
  }, [result, t]);

  const audioTracks = useMemo(() => {
    if (!result?.audio?.length) return [];
    return [
      {
        title: t('checks:result video and audio.title.audio').toUpperCase(),
        segments: result.audio.map((seg) => ({
          startTime: msToSec(seg.start_time_ms),
          endTime: msToSec(seg.end_time_ms),
          status: mapVerdict(seg.verdict, t).value,
          mediaDuration: durationSec,
        })),
      },
    ];
  }, [result, t]);

  const tracks = useMemo(
    () => [...videoTracks, ...audioTracks],
    [videoTracks, audioTracks],
  );

  const currentVideoSegment = useMemo(
    () => (result?.video ? findSegmentForTime(result.video, currentTime) : null),
    [result, currentTime],
  );

  const currentAudioSegment = useMemo(
    () => (result?.audio ? findSegmentForTime(result.audio, currentTime) : null),
    [result, currentTime],
  );

  const mappedVideoVerdict = currentVideoSegment
    ? mapVerdict(currentVideoSegment.verdict, t)
    : null;

  const mappedAudioVerdict = currentAudioSegment
    ? mapVerdict(currentAudioSegment.verdict, t)
    : null;

  const breadcrumbs = (
    <Breadcrumbs>
      <Breadcrumb
        caption={t('checks:check page title')}
        href="/checks"
      />
      <Breadcrumb caption={t('checks:check video audio.breadcrumbs')} />
    </Breadcrumbs>
  );

  return (
    <div className={st.Root}>
      <div className={st.Main}>
        <Page
          breadcrumbs={breadcrumbs}
          title={t('checks:check video audio.title')}
        >
          {videoAudioModal.wrap(
            <VideoAndAudioForm onCloseModal={videoAudioModal.close} />,
          )}
          <div className={st.PlayerWrapper}>
            <div className={cn(st.Player,
              isAudioFile && st.Player_hidden)}>
              {!playerSrc && (
                <div className={st.Loader}>
                  <Loader />
                </div>
              )}
              {!!playerSrc && (
                <VideoJSPlayer
                  className={st.Player}
                  currentTime={currentTime}
                  onPlayingChange={setPlaying}
                  onTimeUpdateSec={setCurrentTime}
                  playing={playing}
                  src={playerSrc}
                />
              )}
            </div>

            {isAudioFile && (
              <div className={st.AudioStub}>
                <img
                  alt="segment"
                  src={segment}
                />
              </div>
            )}

            <ButtonScrimGroup>
              <Control.IconButton
                data-testid="open-modal"
                icon={<RepeatIcon />}
                kind="secondary"
                onClick={videoAudioModal.open}
                shape="square"
                size="small"
                variant="flat"
              />
            </ButtonScrimGroup>
          </div>
        </Page>

        {!!result && (
          <div className={st.PlayerActions}>
            <div className={st.ButtonWrapper}>
              <Control.IconButton
                data-testid="video-audio-repeat-check"
                icon={playing ? <PauseIcon /> : <PlayFilledIcon />}
                kind="primary"
                onClick={() => setPlaying((prev) => !prev)}
                shape="round"
                size="small"
                variant="filled"
              />
              <div className={st.ButtonWrapper_time}>
                <p className="Body-2">{formatTime(currentTime)}</p>
                <div className={st.Divider_vertical} />
                <p className={st.Duration}>{formatTime(durationSec)}</p>
              </div>
            </div>

            <Timeline
              className={st.Timeline}
              currentTime={currentTime}
              duration={durationSec}
              isAudioOnly={isAudioResultOnly}
              onChange={setCurrentTime}
              tracks={tracks}
            />
          </div>
        )}
      </div>

      <ResultPanel
        className={st.Sidebar}
        frameTime={formatTime(currentTime)}
        sourceFileName={fileName}
      >
        {!result && !error ? <div className={st.Loader}>
          <Loader />
        </div> :
          <React.Fragment>
            <div className={st.Divider} />

            {!!hasVideo && (
              <React.Fragment>
                <div className={st.Field}>
                  <div className={st.FieldLabel}>
                    {t('checks:result video and audio.title.video')}
                  </div>
                  {!!mappedVideoVerdict && !!currentVideoSegment && (
                    <div className={st.FieldValue}>
                      <Tag
                        className={st[`VerdictTag_${mappedVideoVerdict.tagKind}`]}
                        icon={mappedVideoVerdict.icon}
                        shape="square"
                        size="medium"
                        value={mappedVideoVerdict.labelVideo}
                      />
                      <div className={st.Threshold}>
                        <span className="Body-2">
                          {Math.floor(currentVideoSegment.confidence * 100)}
                          %
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <div className={st.Divider} />
              </React.Fragment>
            )}

            {!!hasAudio && (
              <div className={st.Field}>
                <div className={st.FieldLabel}>
                  {t('checks:result video and audio.title.audio')}
                </div>
                {!!mappedAudioVerdict && !!currentAudioSegment && (
                  <div className={st.FieldValue}>
                    <Tag
                      className={st[`VerdictTag_${mappedAudioVerdict.tagKind}`]}
                      icon={mappedAudioVerdict.icon}
                      shape="square"
                      size="medium"
                      value={mappedAudioVerdict.labelAudio}
                    />
                    <div className={st.Threshold}>
                      <span className="Body-2">
                        {Math.floor(currentAudioSegment.confidence * 100)}
                        %
                      </span>
                    </div>
                  </div>
                )}

              </div>
            )}

          </React.Fragment>}
      </ResultPanel>

    </div>
  );
}

export const VideoAndAudioResultPage = VideoAndAudioResultPageComponent;
